export interface ApiPolicyListFilterQuery {
    filter?: string;
}

export interface ApiPolicyListItem {
    id: number;
    name: string;
    description?: string;
}

export interface ApiPolicyResponse extends ApiPolicyListItem {
    guid: string;
    tags?: string[];
    allowPublicAccess: boolean;
    publicAccessDeployments?: ApiPolicyDeploymentResponse[];
    targetEndpoints: ApiPolicyTargetEndpointResponse[];
    identities: ApiPolicyIdentityResponse[];
    apiKeyGroups: ApiPolicyApiKeyGroupResponse[];
    requestLoggingOptions: ApiPolicyRequestLoggingOptionResponse[],
    modifier: string;
    modifiedUtc: string;
    deletedUtc?: string;
    apiKeyName?: string;
    apiKeyLocation?: ApiKeyLocationIn;
}

export type ApiPolicyView = Partial<Pick<ApiPolicyResponse, 'id' | 'name' | 'description' | 'tags' | 'allowPublicAccess' | 'modifier' | 'modifiedUtc' | 'apiKeyName' | 'apiKeyLocation'>>
    & {
        publicAccessDeployments?: ApiPolicyDeploymentView[];
        targetEndpoints?: ApiPolicyTargetEndpointView[],
        identities?: ApiPolicyIdentityView[];
        apiKeyGroups?: ApiPolicyApiKeyGroupView[];
        requestLoggingOptions?: ApiPolicyRequestLoggingOptionView[],
        dirty?: boolean;
        valid?: boolean;
    };

interface ApiPolicyTargetEndpointResponse {
    id: number;
    path: string;
    method: PolicyEndpointMethod;
    requestLimit: number;
    requestLimitWindowInMinutes: ThrottlingTimeUnit;
}

export enum PolicyEndpointMethod {
    ALL = 'All',
    GET = 'Get',
    POST = 'Post',
    PUT = 'Put',
    HEAD = 'Head',
    DELETE = 'Delete',
    PATCH = 'Patch',
    OPTIONS = 'Options',
    CONNECT = 'Connect',
    TRACE = 'Trace',
}

export enum ThrottlingTimeUnit {
    Disabled = 0,
    Minute = 1,
    Hour = 60,
    Day = 60 * 24,
}

export type ApiPolicyTargetEndpointView = Partial<ApiPolicyTargetEndpointResponse>

interface ApiPolicyIdentityResponse {
    id: number;
    isPrivateApplication: boolean;
    issuers: string[];
    name: string;
    priorityNumber?: number;
    throttlingByClaim?: string,
    requestLimit?: number;
    requestLimitWindowInMinutes?: ThrottlingTimeUnit;
    identityRules: ApiPolicyIdentityRuleResponse[];
    deployments: ApiPolicyDeploymentResponse[];
}

export type ApiPolicyIdentityView = Partial<Pick<ApiPolicyIdentityResponse, 'id' | 'name' | 'priorityNumber' | 'isPrivateApplication' | 'issuers' | 'throttlingByClaim' | 'requestLimit' | 'requestLimitWindowInMinutes'>>
    & {
        identityRules?: ApiPolicyIdentityRuleView[];
        deployments?: ApiPolicyDeploymentView[];
    }

export enum ApiPolicyIdentityType {
    OAuth,
    PrivateApplication,
    ApiKey
}

interface ApiPolicyIdentityRuleResponse {
    id: number;
    ruleType: ApiAccessRuleType;
    claim: string;
    value: string;
    matchType: IdentityRuleMatchType;
}

export type ApiPolicyIdentityRuleView = Partial<ApiPolicyIdentityRuleResponse>

export enum ApiAccessRuleType {
    Allow = 'Allow',
    Deny = 'Deny'
}

export enum IdentityRuleMatchType {
    Exact = 'Exact',
    Exists = 'Exists',
    Regex = 'Regex',
}

interface ApiPolicyDeploymentResponse {
    id: number;
    agentGroupName: string;
    deployerUserName: string;
    deployedUtc: string;
}

export type ApiPolicyDeploymentView = Partial<ApiPolicyDeploymentResponse>

interface ApiPolicyApiKeyGroupResponse {
    id: number;
    name: string;
    requestLimit?: number;
    requestLimitWindowInMinutes?: ThrottlingTimeUnit;
    apiPolicyApiKeyGroupAssignments: ApiPolicyApiKeyGroupAssignmentResponse[];
}

export enum ApiKeyLocationIn {
    Header = 'Header',
    Query = 'Query'
}

interface ApiPolicyApiKeyGroupAssignmentResponse {
    id: number;
    apiKey: ApiKeyResponse;
}

export interface ApiKeyResponse {
    id: number;
    name: string;
    environment: string;
}

export type ApiPolicyApiKeyGroupView = Partial<ApiPolicyApiKeyGroupResponse>

interface ApiPolicyRequestLoggingOptionResponse {
    id: number;
    name: string;
    disableLogging: boolean;
    deployments: ApiPolicyDeploymentResponse[];
    loggingFields: LoggingField[];
    ipLogging: IPLogging;
    requestBodyMaxLengthKb: BodyLoggingLengthInKB;
    responseBodyMaxLengthKb: BodyLoggingLengthInKB;
}

export type ApiPolicyRequestLoggingOptionView = Partial<Pick<ApiPolicyRequestLoggingOptionResponse, 'requestBodyMaxLengthKb' | 'responseBodyMaxLengthKb' | 'id' | 'ipLogging' | 'disableLogging' | 'loggingFields' | 'name'>> &  {
    deployments?: ApiPolicyDeploymentView[];
}

export enum LoggingField {
    Identity = "Identity",
    QueryParameters = "QueryParameters",
    RequestHeaders = "RequestHeaders",
    ResponseHeaders = "ResponseHeaders",
    ErrorResponseBody = "ErrorResponseBody",
    RequestBody = "RequestBody",
    ResponseBody = "ResponseBody",
}

export enum IPLogging {
    ClientIP = "ClientIP",
    ClientIPXForwardedForFirst = "ClientIPXForwardedForFirst",
    ClientIPXForwardedForAll = "ClientIPXForwardedForAll",
    Disable = "Disable"
}

export enum BodyLoggingLengthInKB {
    One = 1,
    Ten = 10,
    Hundred = 100,
}

export interface IssuerResponse {
    name: string;
    displayName: string;
}

export interface ApiAccessManagementResponse {
    apiKeys: ApiKeyResponse[];
}
